import { useEffect, useState } from "react";

export default function FlickerScreen({ config, onFinish }) {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("show"); // "show" | "blank"
  const [showInput, setShowInput] = useState(false);
  const [userAnswer, setUserAnswer] = useState("");

  const { sequence, answer: correctAnswer } = config;
  const isMul = config.type === "multiplication";
  const isDiv = config.type === "division";
  const totalFrames = isMul ? (sequence.length * 2 - 1) : isDiv ? 3 : sequence.length;

  // Blank gap duration — user-configured Wait Time (falls back to speed if not set)
  const blankDuration = config.waitTime ?? config.speed;

// RESET
useEffect(() => {
  setIndex(0);
  setPhase("show");
  setShowInput(false);
  setUserAnswer("");
}, [config.sequence]);

// ANIMATION — addition: show → blank gap → next number
//             mul/div:  show → next number (no blank)
// Last number always goes directly to input.
useEffect(() => {
  if (!sequence || sequence.length === 0) return;
  if (showInput) return;

  const isLastNumber = index === totalFrames - 1;
  const useBlank = !isMul && !isDiv; // blank gap only for addition

  if (phase === "show") {
    if (isLastNumber) {
      // Last frame: go straight to answer input
      const t = setTimeout(() => setShowInput(true), config.speed);
      return () => clearTimeout(t);
    } else if (useBlank) {
      // Addition only: transition to blank gap before next number
      const t = setTimeout(() => setPhase("blank"), config.speed);
      return () => clearTimeout(t);
    } else {
      // Multiplication / Division: skip blank, go straight to next frame
      const t = setTimeout(() => {
        setIndex((prev) => prev + 1);
      }, config.speed);
      return () => clearTimeout(t);
    }
  } else {
    // phase === "blank" (addition only): show empty circle, then next number
    const t = setTimeout(() => {
      setIndex((prev) => prev + 1);
      setPhase("show");
    }, blankDuration);
    return () => clearTimeout(t);
  }
}, [index, phase, sequence, config.speed, totalFrames, blankDuration, showInput, isMul, isDiv]);

const handleSubmit = () => {
  const expression = sequence
    .map((item, i) => {
      if (isMul) {
        return i === 0 ? `${item.value}` : `× ${item.value}`;
      }
      if (isDiv) {
        return i === 0 ? `${item.value}` : `÷ ${item.value}`;
      }
      return item.type === "add"
        ? `${i === 0 ? "" : "+"}${item.value}`
        : `-${item.value}`;
    })
    .join(" ");

  onFinish({
    correct: correctAnswer,
    user: Number(userAnswer),
    expression,
    type: config.type || "addition",
  });
};

  // Helper to get what to show in the circle
  const getDisplayContent = () => {
    // During blank phase — return nothing (empty circle)
    if (phase === "blank") return null;

    if (isDiv) {
      if (index === 0) return sequence[0].value;
      if (index === 1) return "÷";
      if (index === 2) return sequence[1].value;
    }

    if (!isMul) {
      return (
        <>
          {index > 0 && (sequence[index]?.type === "add" ? "+" : "-")}
          {sequence[index]?.value}
        </>
      );
    }

    if (index % 2 === 0) {
      return sequence[index / 2]?.value;
    } else {
      return "×";
    }
  };

  return (
    <div className="flicker-container">

      {!showInput ? (
        <>
          <h3 className="title">
            {isMul ? "Multiplication" : isDiv ? "Division" : "Addition"}
          </h3>

          {/* Circle: blank (empty blue) during gap, number during show */}
          <div
            key={`${index}-${phase}`}
            className={`circle${phase === "blank" ? " circle-blank" : ""}`}
            style={{ animationDuration: `${config.speed * 0.8}ms` }}
          >
            {getDisplayContent()}
          </div>

          <button className="exit-btn">EXIT</button>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(index / totalFrames) * 100}%` }}
            />
          </div>
        </>
      ) : (
        <div className="flicker-container">
          <h3 className="title">
            {isMul ? "Multiplication" : isDiv ? "Division" : "Addition"}
          </h3>
          <div className="circle input-circle">
            <div className="input-group">
              <input
                type="number"
                placeholder="Answer"
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                autoFocus
              />
              <div className="line"></div>
            </div>
            <button className="submit-btn" onClick={handleSubmit}>
              Submit
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
